import React, { useCallback, useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import moment from 'moment';
import { DashboardContainer } from '../../Containers/DashboardLayout';
import { Toasts } from '../../Components/Toasts/Toasts';
import { StatCard } from './components/StatCard';
import { fetchStats } from './saga/saga';

const Section = ({ title, children }) => (
  <div className="mt-6">
    <h4 className="text-[16px] font-semibold mb-2 text-gray-700">{title}</h4>
    <div className="flex flex-wrap gap-3">{children}</div>
  </div>
);

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchStats();
      setStats(data);
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <DashboardContainer>
      <div className="p-4 max-w-[1100px]">
        <div className="flex items-center justify-between">
          <h3 className="text-[22px] font-bold">Dashboard</h3>
          <Button onClick={load} disabled={loading} variant="outlined" size="small">
            {loading ? <CircularProgress size={16} /> : 'Refresh'}
          </Button>
        </div>

        <Section title="Users">
          <StatCard label="Total" value={stats?.users.total} />
          <StatCard label="Anonymous" value={stats?.users.anonymous} />
          <StatCard label="Admins" value={stats?.users.admins} />
          <StatCard label="Live (10 min)" value={stats?.users.liveLast10Min} />
          <StatCard label="Blocked" value={stats?.users.blocked} />
          <StatCard label="Deactivated" value={stats?.users.deactivated} />
          <StatCard label="Email bouncing" value={stats?.users.emailBouncing} />
        </Section>

        <Section title="Signups">
          <StatCard label="Today" value={stats?.signups.today} />
          <StatCard label="Yesterday" value={stats?.signups.yesterday} />
          <StatCard label="Last 7 days" value={stats?.signups.last7Days} />
          <StatCard label="Last 30 days" value={stats?.signups.last30Days} />
        </Section>

        <Section title="Active users">
          {/* Cumulative tiers — each card includes the prior one's cohort.
              `Active` is the rolling 10-minute window; `Today` adds anyone
              seen since 00:00 UTC (and the trailing 10 min, which catches
              cross-midnight pings); `Yesterday` adds anyone seen since
              00:00 UTC yesterday; etc. Same number can appear in multiple
              cards by design. */}
          <StatCard label="Active (10 min)" value={stats?.activity.live} />
          <StatCard label="Today" value={stats?.activity.today} />
          <StatCard label="Yesterday" value={stats?.activity.yesterday} />
          <StatCard label="Last 7 days" value={stats?.activity.last7Days} />
          <StatCard label="Last 30 days" value={stats?.activity.last30Days} />
        </Section>

        <Section title="Posts">
          <StatCard label="Today" value={stats?.posts.today} />
          <StatCard label="Last 7 days" value={stats?.posts.last7Days} />
          <StatCard label="Total" value={stats?.posts.total} />
        </Section>

        <Section title="Messages">
          <StatCard label="Today" value={stats?.messages.today} />
          <StatCard label="Last 7 days" value={stats?.messages.last7Days} />
        </Section>

        <div className="mt-8">
          <h4 className="text-[16px] font-semibold mb-2 text-gray-700">Recent signups</h4>
          {!stats?.recentSignups?.length ? (
            <div className="text-[14px] text-gray-500">No recent signups.</div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              {/* min-w keeps each column readable on small screens; the
                  parent's overflow-x-auto turns the row into a horizontal
                  scroll instead of squishing the last column. */}
              <table className="w-full text-left text-[14px]" style={{ minWidth: 640 }}>
                <thead className="bg-gray-50 text-gray-600 text-[12px] uppercase">
                  <tr>
                    <th className="px-3 py-2 whitespace-nowrap">Name</th>
                    <th className="px-3 py-2 whitespace-nowrap">Email</th>
                    <th className="px-3 py-2 whitespace-nowrap">Phone</th>
                    <th className="px-3 py-2 whitespace-nowrap">Signed up</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recentSignups.map((r) => (
                    <tr key={r.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 whitespace-nowrap">{r.name || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.email || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.phone || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                        {moment(r.createdAt).fromNow()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-8">
          <h4 className="text-[16px] font-semibold mb-2 text-gray-700">
            Languages
          </h4>
          <p className="text-[12px] text-gray-500 mb-2">
            Auto-detected from each user's browser on first visit (overridable
            in Settings). Use this to decide which translations to ship next.
          </p>
          {!stats?.languageBreakdown?.length ? (
            <div className="text-[14px] text-gray-500">No language data yet.</div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-left text-[14px]" style={{ minWidth: 320 }}>
                <thead className="bg-gray-50 text-gray-600 text-[12px] uppercase">
                  <tr>
                    <th className="px-3 py-2 whitespace-nowrap">Language</th>
                    <th className="px-3 py-2 whitespace-nowrap text-right">Users</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.languageBreakdown.map((row) => (
                    <tr key={row.language || '__null__'} className="border-t border-gray-100">
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-[13px]">
                        {row.language || <span className="text-gray-400">unknown</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-right">
                        {row.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardContainer>
  );
};
